import React, { useMemo, useState } from "react";
import {
  Paper,
  Typography,
  Card,
  CardActionArea,
  Box,
  Button,
  IconButton,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  Divider,
  Chip,
  Tooltip,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchOffIcon from "@mui/icons-material/SearchOff";

import PeopleIcon from "@mui/icons-material/People";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import DescriptionIcon from "@mui/icons-material/Description";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessIcon from "@mui/icons-material/Business";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import SummarizeIcon from "@mui/icons-material/Summarize";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

import { useNavigate } from "react-router-dom";

/* ---------------------------------------------------------------
   Report definitions, grouped by purpose.
   Each group has its own accent colour so users can scan quickly.
---------------------------------------------------------------- */
const REPORT_GROUPS = [
  {
    key: "masters",
    title: "Master Lists",
    subtitle: "Customer, vendor and vehicle records",
    color: "#1a73e8",
    tint: "#e8f1ff",
    reports: [
      {
        title: "List of Customers",
        desc: "All customer master records",
        icon: <PeopleIcon />,
        path: "/reports/customers",
      },
      {
        title: "List of Vendors",
        desc: "All vendor master records",
        icon: <BusinessIcon />,
        path: "/reports/vendors",
      },
      {
        title: "List of Vehicles",
        desc: "All vehicle master records",
        icon: <DirectionsCarIcon />,
        path: "/reports/vehicles",
      },
      {
        title: "Tariff Print",
        desc: "Vehicle tariff details",
        icon: <DescriptionIcon />,
        path: "/reports/tariff-print",
      },
    ],
  },
  {
    key: "operations",
    title: "Trip & Operations",
    subtitle: "Bookings, fuel and trip-wise statements",
    color: "#0f9d8a",
    tint: "#e3f6f3",
    reports: [
      {
        title: "Bookings",
        desc: "Booking reports",
        icon: <AssignmentTurnedInIcon />,
        path: "/reports/bookings",
      },
      {
        title: "Fuel Indents",
        desc: "Fuel issue & usage",
        icon: <LocalGasStationIcon />,
        path: "/reports/fuel-indents",
      },
      {
        title: "Trip Statement",
        desc: "Trip wise statement",
        icon: <AssessmentIcon />,
        path: "/reports/trip-statement",
      },
      {
        title: "Comparison Statement",
        desc: "Trip wise comparison statement",
        icon: <CompareArrowsIcon />,
        path: "/reports/comparison-statement",
      },
      {
        title: "Batta Statement",
        desc: "Trip wise driver batta statement",
        icon: <ReceiptLongIcon />,
        path: "/reports/batta-statement",
      },
    ],
  },
  {
    key: "finance",
    title: "Accounts & Compliance",
    subtitle: "Sales, purchases, GST and business analytics",
    color: "#e8710a",
    tint: "#fdeee0",
    reports: [
      {
        title: "Sales Register",
        desc: "All sales summary",
        icon: <PointOfSaleIcon />,
        path: "/reports/sale-register",
      },
      {
        title: "Purchase Register",
        desc: "All purchases summary",
        icon: <ShoppingCartIcon />,
        path: "/reports/purchase-register",
      },
      {
        title: "GST Report",
        desc: "GST summary & filing",
        icon: <AccountBalanceIcon />,
        path: "/reports/gst-report",
      },
      {
        title: "MIS Reports",
        desc: "Business analytics",
        icon: <SummarizeIcon />,
        path: "/reports/mis-reports",
      },
    ],
  },
];

const TOTAL_REPORTS = REPORT_GROUPS.reduce((n, g) => n + g.reports.length, 0);

/* ---------------------------------------------------------------
   Single report card
---------------------------------------------------------------- */
function ReportCard({ report, color, tint, onOpen }) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        borderColor: "divider",
        transition: "box-shadow .2s, border-color .2s, transform .2s",
        "&:hover": {
          borderColor: color,
          boxShadow: "0 6px 18px rgba(16,24,40,.10)",
          transform: "translateY(-2px)",
        },
        "&:hover .report-arrow": {
          opacity: 1,
          transform: "translateX(0)",
        },
      }}
    >
      <CardActionArea
        onClick={onOpen}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 2,
          p: 2,
          minHeight: 96,
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            flexShrink: 0,
            borderRadius: 2,
            background: tint,
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {report.icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={700} fontSize={15} noWrap>
            {report.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {report.desc}
          </Typography>
        </Box>

        <ChevronRightIcon
          className="report-arrow"
          sx={{
            color,
            opacity: 0,
            transform: "translateX(-6px)",
            transition: "all .2s",
          }}
        />
      </CardActionArea>
    </Card>
  );
}

/* ---------------------------------------------------------------
   Page
---------------------------------------------------------------- */
export default function ReportsDashboard() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  // Go to the previous page; if the user opened this page directly
  // (no history), fall back to the home dashboard.
  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return REPORT_GROUPS;
    return REPORT_GROUPS.map((g) => ({
      ...g,
      reports: g.reports.filter(
        (r) =>
          r.title.toLowerCase().includes(q) || r.desc.toLowerCase().includes(q)
      ),
    })).filter((g) => g.reports.length > 0);
  }, [query]);

  const visibleCount = filteredGroups.reduce((n, g) => n + g.reports.length, 0);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* ---------- Header: back + title + search ---------- */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Tooltip title="Go back">
            <IconButton
              onClick={handleBack}
              aria-label="Go back"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                "&:hover": { background: "#e8f1ff", color: "#1a73e8" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>

          <Box>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
              sx={{ mb: 0.25 }}
            >
              <Link
                component="button"
                type="button"
                underline="hover"
                color="text.secondary"
                variant="body2"
                onClick={() => navigate("/")}
              >
                Dashboard
              </Link>
              <Typography variant="body2" color="text.primary" fontWeight={600}>
                Reports
              </Typography>
            </Breadcrumbs>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h5" fontWeight={800}>
                Reports
              </Typography>
              <Chip
                size="small"
                label={`${TOTAL_REPORTS} reports`}
                sx={{ fontWeight: 600, background: "#eef2f7" }}
              />
            </Box>
          </Box>
        </Box>

        <TextField
          size="small"
          placeholder="Search reports"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ width: { xs: "100%", sm: 300 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setQuery("")}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: { borderRadius: 2 },
          }}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* ---------- Grouped report cards ---------- */}
      {filteredGroups.map((group) => (
        <Box key={group.key} sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 4,
                height: 32,
                borderRadius: 2,
                background: group.color,
              }}
            />
            <Box>
              <Typography fontWeight={800} fontSize={17} lineHeight={1.2}>
                {group.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {group.subtitle}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: 2.5,
            }}
          >
            {group.reports.map((r) => (
              <ReportCard
                key={r.path}
                report={r}
                color={group.color}
                tint={group.tint}
                onOpen={() => navigate(r.path)}
              />
            ))}
          </Box>
        </Box>
      ))}

      {/* ---------- Empty state ---------- */}
      {visibleCount === 0 && (
        <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
          <SearchOffIcon sx={{ fontSize: 48, mb: 1, opacity: 0.6 }} />
          <Typography fontWeight={700} color="text.primary">
            No reports match "{query}"
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Try a different name, or clear the search to see all reports.
          </Typography>
          <Button variant="outlined" onClick={() => setQuery("")}>
            Clear search
          </Button>
        </Box>
      )}
    </Paper>
  );
}
